import React from "react";
import { Loader2, RefreshCw, Wrench, CheckCircle2, XCircle, Clock, Info } from "lucide-react";

function StateBadge({ ok, label }) {
  if (ok) return <span className="inline-flex items-center gap-1 text-[11px] text-emerald-400 mono"><CheckCircle2 size={11} /> {label}</span>;
  if (label === "missing" || label === "mismatch") return <span className="inline-flex items-center gap-1 text-[11px] text-red-400 mono"><XCircle size={11} /> {label}</span>;
  return <span className="inline-flex items-center gap-1 text-[11px] text-amber-400 mono"><Clock size={11} /> {label}</span>;
}

function MatrixRow({ row }) {
  const cfLabel = row.cf_ok ? "written" : (row.cf_record_id ? "mismatch" : "missing");
  return (
    <tr className="border-t border-zinc-800/60 hover:bg-zinc-900/40" data-testid={`infobip-row-${row.kind}`}>
      <td className="px-3 py-2 align-top">
        <div className="text-zinc-200 font-medium">{row.label}</div>
        {row.notes && (
          <div className="mt-1 flex items-start gap-1 text-[10px] text-amber-400/80">
            <Info size={10} className="mt-[1px] shrink-0" /><span>{row.notes}</span>
          </div>
        )}
      </td>
      <td className="px-3 py-2 mono text-zinc-400">{row.type}</td>
      <td className="px-3 py-2 mono text-zinc-400 break-all max-w-[280px]">{row.name}</td>
      <td className="px-3 py-2">
        <StateBadge ok={row.cf_ok} label={cfLabel} />
        {row.expected && (
          <details className="mt-1">
            <summary className="text-[10px] text-zinc-500 cursor-pointer hover:text-zinc-300">expected</summary>
            <div className="mono text-[10px] text-zinc-500 break-all mt-1 max-w-[240px]">{row.expected}</div>
            {row.cf_content && row.cf_content !== row.expected && (
              <div className="mono text-[10px] text-amber-400/80 break-all mt-1 max-w-[240px]">actual: {row.cf_content}</div>
            )}
          </details>
        )}
      </td>
      <td className="px-3 py-2">
        <StateBadge ok={row.provider_verified} label={row.provider_verified ? "verified" : (row.provider_status || "pending")} />
      </td>
    </tr>
  );
}

const btn = "inline-flex items-center gap-1 px-3 py-1.5 rounded-sm text-xs font-medium disabled:opacity-50";

export function InfobipDnsMatrix({ matrix, status, busy, busyRow, fixDns, verifyAll, refreshMatrix }) {
  return (
    <div className="mt-2">
      <div className="flex items-center justify-between mb-2">
        <div className="label-tiny">DNS records ({matrix.length}){status?.active ? " · domain active" : ""}</div>
        <div className="flex items-center gap-2">
          <button data-testid="infobip-fix-dns-btn" onClick={fixDns} disabled={busy} className={`${btn} border border-sky-700 text-sky-300 hover:border-sky-400 hover:text-sky-200`} title="Write any missing records into Cloudflare">
            {busyRow === "all" ? <Loader2 size={12} className="animate-spin" /> : <Wrench size={12} />} Fix DNS
          </button>
          <button data-testid="infobip-verify-all-btn" onClick={verifyAll} disabled={busy} className={`${btn} border border-zinc-700 hover:border-white text-zinc-300 hover:text-white`} title="Trigger Infobip verification">
            {busyRow === "verify" ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />} Verify all
          </button>
          <button data-testid="infobip-refresh-matrix-btn" onClick={refreshMatrix} disabled={busy} className="inline-flex items-center gap-1 text-zinc-400 hover:text-zinc-100 px-2 py-1.5 rounded-sm text-xs disabled:opacity-50" title="Refresh matrix">
            <RefreshCw size={12} />
          </button>
        </div>
      </div>
      <div className="border border-zinc-800 rounded-sm overflow-hidden" data-testid="infobip-dns-matrix">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-zinc-950/60 text-zinc-500 uppercase tracking-wider text-[10px]">
              {["Record", "Type", "Name", "Cloudflare", "Infobip"].map((h) => <th key={h} className="text-left px-3 py-2">{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {matrix.length === 0 && (
              <tr><td colSpan={5} className="px-3 py-4 text-center text-zinc-500 mono">{busy ? "Loading matrix…" : "No DNS matrix yet — hit \"Verify all\" to fetch"}</td></tr>
            )}
            {matrix.map((row) => <MatrixRow key={row.kind} row={row} />)}
          </tbody>
        </table>
      </div>
    </div>
  );
}
